<template>
  <full-screen
    :title="t('end-shift.title')"
    :show-back="isMobile"
    :show-close="!isMobile"
    @back="goBack"
    @close="goBack"
  >
    <div class="flex h-full w-full">
      <div
        class="w-full sm:w-1/2 shrink-0 bg-n-700 p-4 sm:p-10 flex-col sm:flex"
        :class="{ flex: draftState.currentStep === null, hidden: draftState.currentStep !== null }"
      >
        <div>
          <div class="text-n-0 mb-6">
            <div class="mb-1 capitalize heading-3">
              {{ closingTill.name }}
            </div>
            <div class="font-body">{{ t('end-shift.shift-summary') }}</div>
          </div>
          <close-till-methods
            :open-methods="openMethods"
            :current-step="draftState.currentStep"
            :amounts="confirmedAmounts"
            @step="onStep"
          />
        </div>

        <div v-if="!config.blindTill" class="mt-auto">
          <l-button size="medium" icon="printer" type="text" @click="fetchAndPrintTillReport">
            {{ t('end-shift.print-till-report') }}
          </l-button>
        </div>
      </div>

      <div
        v-if="currentMethod && currentMethodState"
        class="w-full sm:w-1/2 shrink-0 bg-n-800 p-4 sm:p-10 flex-col h-full min-h-0 sm:flex"
        :class="{ flex: draftState.currentStep !== null, hidden: draftState.currentStep === null }"
      >
        <div class="flex justify-between mb-6">
          <div class="text-n-0 capitalize heading-3">
            {{ te(`bill.${currentMethod}`) ? t(`bill.${currentMethod}`) : currentMethod }}
          </div>
          <div v-if="!config.blindTill">
            <l-button size="small" type="text" @click="seeMovements">
              {{ t('end-shift.see-movements') }}
            </l-button>
          </div>
        </div>
        <div class="flex-1 min-h-0">
          <cash-amount
            :key="currentMethod"
            :method-name="currentMethod"
            v-model="currentMethodState.amount"
            v-model:bills-amounts="currentMethodState.billsAmounts"
            v-model:selected-tab="currentMethodState.selectedTab"
          >
            <div class="flex justify-end flex-col gap-2 shrink-0 mt-4">
              <l-button class="w-full" @click="continueAction">
                {{ doneLabel }}
              </l-button>
            </div>
          </cash-amount>
        </div>
      </div>
    </div>

    <l-modal
      :open="!!selectedReport"
      :title="t('end-shift.title')"
      :button-text="t('ctas.continue')"
      :secondary-button="true"
      :backdrop-closable="false"
      :show-close="false"
      :secondary-button-text="t('report.print-shift-movements')"
      @action="closeReport"
      @secondary-action="printShiftMovements"
      @close="closeReport"
    >
      <div>
        <div class="flex justify-between items-center mb-4">
          <div class="font-body text-sm text-n-200">
            {{ t('report.difference-since') }}:
            {{ format(selectedReport!.start, 'dd/MM/yyyy - HH:mm') }}
          </div>
          <div :class="{ 'text-g-500': diff === 0, 'text-r-500': diff != 0 }" class="heading-3">
            <span v-if="diff > 0">+ </span>{{ currency(diff) }}
          </div>
        </div>
        <till-report-preview v-if="tillReport" :till-report="tillReport" :name="closingTill.name" />
      </div>
    </l-modal>

    <l-modal
      v-if="currentMethod && currentMethodStart !== null"
      v-model:open="seeingMovements"
      :title="`${currentMethod} ${t('end-shift.movements')}`"
      :button-text="t('end-shift.print-movements')"
      @close="seeingMovements = false"
      @action="printCurrentMethodMovements"
    >
      <div class="pb-4">
        {{ t('end-shift.start-amount') + ' : ' + currency(currentMethodStart) }}
      </div>
      <close-till-method-movements
        :movements="movements[currentMethod] ?? []"
        :current-method="currentMethod"
      />
    </l-modal>
  </full-screen>
</template>

<script setup lang="ts">
import { differenceInHours, format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LButton, LModal, useDialog } from '@last/core-ui/paprika'

import { reportsApi } from '@/api/client'
import FullScreen from '@/components/FullScreen.vue'
import { useMoney } from '@/composables/useMoney'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useTillStore } from '@/store/till'
import sync from '@/sync/service'
import type {
  CloseTillDraft,
  OpenMethodWithCurrentAmount,
  PaymentMethodType,
  ShiftMovement,
  Till,
  TillReport
} from '@/types'
import { isMobile } from '@/utils/device-type'

import CashAmount from './CashAmount.vue'
import CloseTillMethodMovements from './CloseTillMethodMovements.vue'
import CloseTillMethods from './CloseTillMethods.vue'
import TillReportPreview from './TillReportPreview.vue'

type EmployeeTill = {
  id: string
  name: string
}

const dialog = useDialog()
const { t, te } = useI18n()
const { currency } = useMoney()
const route = useRoute()
const router = useRouter()

const configStore = useConfigStore()
const authStore = useAuthStore()
const tillStore = useTillStore()

const { endShift } = tillStore
const { startedShifts } = storeToRefs(tillStore)
const { config, tills } = storeToRefs(configStore)
const { listEmployees } = storeToRefs(authStore)

const tillId = route.params.tillId as string

const openMethods = ref<OpenMethodWithCurrentAmount[]>([])
const tillReport = ref<TillReport | null>(null)
const selectedReport = ref<TillReport | null>(null)
const movements = ref<Record<PaymentMethodType, ShiftMovement[]>>({})
const seeingMovements = ref(false)

const draftState = ref<CloseTillDraft>({
  currentStep: null,
  methods: {},
  savedAt: Date.now()
})

onMounted(async () => {
  if (!startedShifts.value.includes(tillId)) {
    router.replace({ name: 'endShift' })
    return
  }

  openMethods.value = await reportsApi.getOpenMethodsWithCurrentAmount({ tillId })

  const storedDraft = tillStore.closeTillDrafts[tillId]
  const draft =
    storedDraft && differenceInHours(Date.now(), storedDraft.savedAt) < 2 ? storedDraft : null
  if (storedDraft && !draft) {
    tillStore.clearCloseTillDraft(tillId)
  }
  const defaultMethodState = { amount: 0, billsAmounts: [], selectedTab: 'total', confirmed: false }
  for (const { method } of openMethods.value) {
    draftState.value.methods[method] = { ...defaultMethodState, ...draft?.methods[method] }
  }
  draftState.value.currentStep = isMobile.value ? null : (draft?.currentStep ?? 0)

  if (!config.value.blindTill) {
    const movementsResponse = await reportsApi.getLastTillShiftMovements({ tillId })
    movements.value = movementsResponse.reduce(
      (res, movement) => {
        const method = movement.method!
        res[method] = [...(res[method] || []), movement]
        return res
      },
      {} as Record<PaymentMethodType, ShiftMovement[]>
    )
  }
})

const diff = computed(() => {
  if (tillReport.value) {
    return (tillReport.value.endAmount ?? 0) - tillReport.value.expectedEndAmount
  }
  return 0
})

const closingTill = computed(() => {
  const till = tills.value.find(till => till.id === tillId)
  if (!till) {
    const employee = getEmployeeByTill()
    if (employee) {
      return { id: tillId, name: employee.name } as EmployeeTill
    }
  }
  return till as Till
})

const currentMethod = computed(() => {
  if (draftState.value.currentStep === null) return null
  return openMethods.value[draftState.value.currentStep]?.method ?? null
})

const currentMethodStart = computed(() => {
  if (draftState.value.currentStep === null) return null
  return openMethods.value[draftState.value.currentStep]?.start ?? null
})

const currentMethodState = computed(() => {
  return currentMethod.value ? draftState.value.methods[currentMethod.value] : null
})

const confirmedAmounts = computed(() => {
  const result: Record<string, number> = {}
  for (const [method, state] of Object.entries(draftState.value.methods)) {
    if (state.confirmed) {
      result[method] = state.amount
    }
  }
  return result
})

const totalFilledSteps = computed(() => {
  return Object.keys(confirmedAmounts.value).length
})

const currentMethodIsFilled = computed(() => {
  return currentMethodState.value?.confirmed === true
})

const isLastStep = computed(() => {
  return totalFilledSteps.value === openMethods.value.length - 1
})

const doneLabel = computed(() => {
  if (isLastStep.value && !currentMethodIsFilled.value) {
    return t('end-shift.end')
  } else {
    return t('end-shift.next')
  }
})

const amountsList = computed(() => {
  return Object.entries(confirmedAmounts.value).map(([method, amount]) => ({
    method,
    amount
  }))
})

function onStep(value: number) {
  draftState.value.currentStep = value
}

function printCurrentMethodMovements() {
  if (!currentMethod.value) return
  printShiftMovements(
    movements.value[currentMethod.value] ?? null,
    closingTill.value.name + ' ' + currentMethod.value
  )
}

function getEmployeeByTill() {
  return listEmployees.value.find(employee => employee.tillId === tillId) || null
}

function getNextEmptyStep() {
  return openMethods.value.findIndex(method => !draftState.value.methods[method.method]?.confirmed)
}

function printShiftMovements(movements?: ShiftMovement[] | null, name?: string) {
  TicketPrinter.printShiftMovements(
    closingTill.value.id,
    name ? name : closingTill.value.name,
    movements ? movements : null
  )
}

function seeMovements() {
  seeingMovements.value = true
}

function goBack() {
  if (isMobile.value && draftState.value.currentStep !== null) {
    draftState.value.currentStep = null
    return
  }
  draftState.value.savedAt = Date.now()
  tillStore.saveCloseTillDraft(tillId, draftState.value)
  router.back()
}

function closeReport() {
  selectedReport.value = null
  router.replace({ name: 'endShift' })
}

async function getTillReport(tillId: string) {
  tillReport.value = await reportsApi.getLastShift({ tillId })
}

async function continueAction() {
  if (!currentMethodState.value) return
  currentMethodState.value.confirmed = true
  if (totalFilledSteps.value === openMethods.value.length) {
    dialog({
      title: t('end-shift.close-till-confirmation-message'),
      secondaryLabel: t('ctas.cancel'),
      mainLabel: t('end-shift.end'),
      content: amountsList.value
        .map(el => `${t(`bill.${el.method}`, el.method)}: ${currency(el.amount)}`)
        .join('\n'),
      onConfirm() {
        sendEndShift()
      }
    })
  } else {
    draftState.value.currentStep = getNextEmptyStep()
  }
}

function sendEndShift() {
  endShift({
    amounts: amountsList.value,
    tillId: closingTill.value.id
  })
  tillStore.clearCloseTillDraft(tillId)
  sync.observeEnd(async () => {
    await getReports(closingTill.value)
  })
}

async function getReports(till: Till | EmployeeTill) {
  await getTillReport(till.id)
  if (tillReport.value) {
    TicketPrinter.printTillReport(tillReport.value, till.name)
  }
  selectedReport.value = tillReport.value
}

async function fetchAndPrintTillReport() {
  const report = await reportsApi.getLastShift({ tillId: closingTill.value.id })
  if (report) {
    TicketPrinter.printTillReport(report, closingTill.value.name)
  }
}
</script>
